/**
 * Módulos contratados por el comité (Console). "Plan completo" = null en el
 * backend: incluye también los módulos que se agreguen en el futuro. En plan
 * parcial, marcar un módulo arrastra sus dependencias (Facturación → Padrón
 * y Lecturas), igual que `normalizar_modulos` en el backend.
 */
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Blocks, Loader2 } from "lucide-react";

import { useToast } from "../../components/Toast";
import { Card, Cargando } from "../../components/ui";
import { ApiError } from "../../lib/api";
import {
  actualizarModulosEmpresa,
  listarModulos,
  type EmpresaRead,
  type ModuloItem,
} from "./api";

function conDependencias(seleccion: ReadonlySet<string>, catalogo: ModuloItem[]): Set<string> {
  const porCodigo = new Map(catalogo.map((m) => [m.code, m]));
  const resultado = new Set<string>();
  const pendientes = [...seleccion];
  while (pendientes.length > 0) {
    const code = pendientes.pop();
    if (code === undefined || resultado.has(code)) continue;
    resultado.add(code);
    pendientes.push(...(porCodigo.get(code)?.requiere ?? []));
  }
  return resultado;
}

export function ModulosEmpresaCard({ empresa }: { empresa: EmpresaRead }): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();
  const catalogoQ = useQuery({ queryKey: ["superadmin", "modulos"], queryFn: listarModulos });

  const [completo, setCompleto] = useState(empresa.modulos_habilitados === null);
  const [seleccion, setSeleccion] = useState<Set<string>>(
    new Set(empresa.modulos_habilitados ?? []),
  );
  useEffect(() => {
    setCompleto(empresa.modulos_habilitados === null);
    setSeleccion(new Set(empresa.modulos_habilitados ?? []));
  }, [empresa.modulos_habilitados]);

  const catalogo = catalogoQ.data ?? [];
  const efectivos = useMemo(() => conDependencias(seleccion, catalogo), [seleccion, catalogo]);

  /** Módulo marcado → quién lo exige (para bloquear el checkbox). */
  const requeridoPor = useMemo(() => {
    const m = new Map<string, string>();
    for (const mod of catalogo) {
      if (!efectivos.has(mod.code)) continue;
      for (const dep of mod.requiere) {
        if (!m.has(dep)) m.set(dep, mod.titulo);
      }
    }
    return m;
  }, [catalogo, efectivos]);

  const actual = empresa.modulos_habilitados;
  const propuesto = completo ? null : catalogo.filter((m) => efectivos.has(m.code)).map((m) => m.code);
  const sinCambios =
    (actual === null && propuesto === null) ||
    (actual !== null && propuesto !== null && actual.join() === propuesto.join());

  const guardarMut = useMutation({
    mutationFn: () => actualizarModulosEmpresa(empresa.id, propuesto),
    onSuccess: () => {
      toast.success(
        "Módulos actualizados. Los usuarios del comité ven el menú nuevo al volver a entrar.",
      );
      void queryClient.invalidateQueries({ queryKey: ["superadmin", "empresa", empresa.id] });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "No se pudieron guardar los módulos"),
  });

  const toggle = (code: string): void => {
    setSeleccion((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  return (
    <Card>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-neutral-900">
          <Blocks className="h-4 w-4 text-brand-600" strokeWidth={1.5} />
          Módulos contratados
        </h2>
        <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-neutral-700">
          <input
            type="checkbox"
            checked={completo}
            onChange={(e) => setCompleto(e.target.checked)}
            className="h-4 w-4 rounded border-neutral-450 text-brand-600 focus:ring-brand-500"
          />
          Plan completo
        </label>
      </div>

      {catalogoQ.isLoading ? (
        <Cargando inline texto="Cargando catálogo…" />
      ) : (
        <ul className="grid gap-2 sm:grid-cols-2">
          {catalogo.map((m) => {
            const marcado = completo || efectivos.has(m.code);
            const exigidoPor = !completo && !seleccion.has(m.code) ? requeridoPor.get(m.code) : undefined;
            const bloqueado = completo || exigidoPor !== undefined;
            return (
              <li key={m.code}>
                <label
                  className={`flex gap-3 rounded-md border p-3 text-sm transition-colors ${
                    marcado ? "border-brand-200 bg-brand-50/60" : "border-neutral-200"
                  } ${bloqueado ? "cursor-default" : "cursor-pointer hover:border-brand-300"}`}
                >
                  <input
                    type="checkbox"
                    checked={marcado}
                    disabled={bloqueado}
                    onChange={() => toggle(m.code)}
                    className="mt-0.5 h-4 w-4 rounded border-neutral-450 text-brand-600 focus:ring-brand-500 disabled:opacity-60"
                  />
                  <span className="min-w-0">
                    <span className="block font-medium text-neutral-900">{m.titulo}</span>
                    <span className="block text-xs text-neutral-500">{m.descripcion}</span>
                    {exigidoPor && (
                      <span className="mt-1 block text-xs text-brand-700">
                        Incluido por {exigidoPor}
                      </span>
                    )}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      )}

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-neutral-500">
          {completo
            ? "El comité ve todo, incluidos los módulos que se agreguen más adelante."
            : "El comité sólo ve lo marcado. El servidor bloquea el resto."}
        </p>
        <button
          type="button"
          onClick={() => guardarMut.mutate()}
          disabled={sinCambios || guardarMut.isPending || (!completo && efectivos.size === 0)}
          className="inline-flex items-center gap-2 rounded-md bg-brand-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-800 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {guardarMut.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          Guardar módulos
        </button>
      </div>
    </Card>
  );
}
